import React, { useEffect, useRef, useState } from 'react'
import useLocaleText from 'components/useLocaleText'
import styles from './styles.module.css'

interface IMediaReorderGridProps {
  items: string[]
  staticItems?: { id: string; content: React.ReactNode }[]
  disabled: boolean
  rtl: boolean
  onReorder?: (from: number, to: number) => void
  children: (index: number, handleProps: React.HTMLAttributes<HTMLDivElement>) => React.ReactNode
}

interface IDrag {
  from: number
  to: number | null
  pointerId?: number
  x: number
  y: number
  dx: number
  dy: number
}

const literals = {
  instructions: {
    key: 'media_grid_reorder_instructions',
    fallback: {
      en: 'Press Space to pick up, arrow keys to move, Space to drop, or Escape to cancel.',
      he: 'לחצו על רווח להרמה, על החצים להזזה, על רווח להנחה או על Escape לביטול.',
    },
  },
  position: {
    key: 'media_grid_reorder_position',
    fallback: { en: 'Position {position} of {total}', he: 'מיקום {position} מתוך {total}' },
  },
}

/** A wrapping grid uses both pointer axes; the legacy gallery keeps its single-row RBD contract. */
export function MediaReorderGrid({
  items,
  staticItems = [],
  disabled,
  rtl,
  onReorder,
  children,
}: IMediaReorderGridProps) {
  const t = useLocaleText(literals)
  const [drag, setDrag] = useState<IDrag | null>(null)
  const gridRef = useRef<HTMLDivElement>(null)
  const current = useRef<IDrag | null>(null)
  const identity = JSON.stringify(items)

  const update = (next: IDrag | null) => {
    current.current = next
    setDrag(next)
  }

  const finish = (commit: boolean) => {
    const active = current.current
    update(null)
    if (commit && !disabled && active?.to != null && active.from !== active.to) onReorder?.(active.from, active.to)
  }

  const handles = (index: number): React.HTMLAttributes<HTMLDivElement> => ({
    role: 'button',
    tabIndex: disabled ? -1 : 0,
    'aria-disabled': disabled,
    'aria-pressed': drag?.from === index,
    title: t.instructions,
    onPointerDown: (event) => {
      if (disabled || event.button !== 0 || current.current) return
      event.preventDefault()
      event.stopPropagation()
      event.currentTarget.focus()
      event.currentTarget.setPointerCapture(event.pointerId)
      update({ from: index, to: index, pointerId: event.pointerId, x: event.clientX, y: event.clientY, dx: 0, dy: 0 })
    },
    onPointerMove: (event) => {
      const active = current.current
      const grid = gridRef.current
      if (!active || active.pointerId !== event.pointerId || !grid) return
      const bounds = grid.getBoundingClientRect()
      const inside =
        event.clientX >= bounds.left &&
        event.clientX <= bounds.right &&
        event.clientY >= bounds.top &&
        event.clientY <= bounds.bottom
      let to: number | null = null
      let distance = Infinity
      if (inside)
        Array.from(grid.children).forEach((node, index) => {
          const rect = node.getBoundingClientRect()
          const nextDistance = Math.hypot(
            event.clientX - (rect.left + rect.width / 2),
            event.clientY - (rect.top + rect.height / 2)
          )
          if (nextDistance < distance) {
            distance = nextDistance
            to = index < items.length ? index : null
          }
        })
      update({ ...active, to, dx: event.clientX - active.x, dy: event.clientY - active.y })
    },
    onPointerUp: (event) => {
      if (current.current?.pointerId === event.pointerId) finish(true)
    },
    onPointerCancel: () => finish(false),
    onLostPointerCapture: () => {
      if (current.current?.pointerId != null) finish(false)
    },
    onBlur: () => {
      if (current.current && current.current.pointerId == null) finish(false)
    },
    onKeyDown: (event) => {
      if (disabled) return
      const active = current.current
      if (event.key === 'Escape' && active) {
        event.preventDefault()
        event.stopPropagation()
        finish(false)
      } else if (event.key === ' ' || event.key === 'Enter') {
        event.preventDefault()
        if (active) finish(true)
        else update({ from: index, to: index, x: 0, y: 0, dx: 0, dy: 0 })
      } else if (
        active &&
        active.pointerId == null &&
        ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)
      ) {
        event.preventDefault()
        const nodes = Array.from(gridRef.current?.children || [])
        const firstTop = nodes[0]?.getBoundingClientRect().top
        const columns = Math.max(1, nodes.filter((node) => node.getBoundingClientRect().top === firstTop).length)
        const horizontal = event.key === 'ArrowRight' ? 1 : -1
        const delta =
          event.key === 'ArrowDown' ? columns : event.key === 'ArrowUp' ? -columns : horizontal * (rtl ? -1 : 1)
        const to =
          event.key === 'Home'
            ? 0
            : event.key === 'End'
            ? items.length - 1
            : Math.max(0, Math.min(items.length - 1, (active.to ?? index) + delta))
        update({ ...active, to })
        nodes[to]?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' })
      }
    },
  })

  useEffect(() => {
    current.current = null
    setDrag(null)
  }, [identity, disabled, rtl])

  useEffect(() => {
    const cancel = () => update(null)
    window.addEventListener('blur', cancel)
    window.addEventListener('resize', cancel)
    return () => {
      window.removeEventListener('blur', cancel)
      window.removeEventListener('resize', cancel)
    }
  }, [])

  return (
    <>
      <div className={styles.grid} ref={gridRef} dir={rtl ? 'rtl' : 'ltr'}>
        {items.map((url, index) => (
          <div key={`media-${url}`} className={styles.slot} data-target={drag?.to === index && drag.from !== index}>
            <div
              className={styles.tile}
              data-dragging={drag?.from === index}
              style={
                drag?.from === index && drag.pointerId != null
                  ? { transform: `translate(${drag.dx}px, ${drag.dy}px)` }
                  : undefined
              }
            >
              {children(index, handles(index))}
            </div>
          </div>
        ))}
        {staticItems.map(({ id, content }) => (
          <div key={`static-${id}`} className={styles.slot}>
            {content}
          </div>
        ))}
      </div>
      <span className={styles.announcement} role="status" aria-live="polite">
        {drag?.to != null
          ? t.position.replace('{position}', String(drag.to + 1)).replace('{total}', String(items.length))
          : ''}
      </span>
    </>
  )
}
